import { Component, OnInit, OnDestroy } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import {
  NgcCookieConsentService,
  NgcNoCookieLawEvent,
  NgcInitializeEvent,
  NgcStatusChangeEvent,
} from 'ngx-cookieconsent';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UserService } from './services/userService/user.service';
import { GoogleAnalyticsService } from './services/googleAnalytics/google-analytics.service';
/* import { RouterExtService } from "./services/routerExtService/routerurlstate.service"; */

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'app';
  //keep refs to subscriptions to be able to unsubscribe later
  private popupOpenSubscription: Subscription;
  private popupCloseSubscription: Subscription;
  private initializeSubscription: Subscription;
  private statusChangeSubscription: Subscription;
  private revokeChoiceSubscription: Subscription;
  private noCookieLawSubscription: Subscription;
  constructor(
    private platform: Platform,
    private router: Router,
    private ccService: NgcCookieConsentService,
    public authService: UserService,
    /* private routerExtService: RouterExtService, */
    public translate: TranslateService,
    public afAuth: AngularFireAuth,
    private googleAnalytics: GoogleAnalyticsService,
  ) {
    this.initializeApp();

    /**set Default Lang  Obligatoire pour que la traduction fonctionne */
    translate.addLangs(['en', 'fr', 'ru', 'pl', 'es']);
    const browserLang = translate.getBrowserLang();
    if (browserLang == 'fr') {
      this.translate.use('fr');
      this.translate.setDefaultLang('fr');
    } else if (browserLang == 'ru') {
      this.translate.use('ru');
      this.translate.setDefaultLang('ru');
    } else if (browserLang == 'pl') {
      this.translate.use('pl');
      this.translate.setDefaultLang('pl');
    } else if (browserLang == 'es') {
      this.translate.use('es');
      this.translate.setDefaultLang('es');
    } else {
      this.translate.use('en');
      this.translate.setDefaultLang('en');
    }
  }

  initializeApp() {
    this.platform.ready().then(() => {
      this.initialize();
      this.afAuth.auth.onAuthStateChanged((user: firebase.User) => {
        if (user) {
        } else {
          setTimeout(() => {
            this.router.navigateByUrl('login');
          }, 500);
        }
      });
    });
  }

  handleClickSound() {
    let x = <HTMLVideoElement>document.getElementById('myAudio');
    x.play();
  }

  /** Safe check to avoid "Cannot read properties of null (reading 'cookie')" from cookieconsent lib */
  private safeHasConsented(): boolean {
    try {
      return (
        typeof document !== 'undefined' &&
        document !== null &&
        this.ccService.hasConsented()
      );
    } catch {
      return false;
    }
  }

  private safeHasAnswered(): boolean {
    try {
      return (
        typeof document !== 'undefined' &&
        document !== null &&
        this.ccService.hasAnswered()
      );
    } catch {
      return false;
    }
  }

  /** Safe destroy to avoid "Cannot read properties of null (reading 'palette')" when already destroyed */
  private safeDestroy(): void {
    try {
      this.ccService.destroy();
    } catch {
      // Already destroyed or not initialized, ignore
    }
  }

  ngOnInit() {
    // Initialize Google Analytics tracking for current route
    // The service will automatically track route changes (including initial route) via NavigationEnd events
    // Track cookie consent if it was already given
    setTimeout(() => {
      if (this.safeHasConsented()) {
        this.googleAnalytics.trackCookieConsent(true);
      } else if (this.safeHasAnswered() && !this.safeHasConsented()) {
        this.googleAnalytics.trackCookieConsent(false);
      }
    }, 100);

    // subscribe to cookieconsent observables to react to main events
    this.popupOpenSubscription = this.ccService.popupOpen$.subscribe(() => {
      // you can use this.ccService.getConfig() to do stuff...
    });

    this.popupCloseSubscription = this.ccService.popupClose$.subscribe(() => {
      // you can use this.ccService.getConfig() to do stuff...
    });

    this.initializeSubscription = this.ccService.initialize$.subscribe(
      (event: NgcInitializeEvent) => {
        // the cookieconsent is initilializing... Not yet safe to call methods like `NgcCookieConsentService.hasAnswered()`
        console.log(`initializing: ${JSON.stringify(event)}`);
        const hasConsented = event.status === 'allow';
        this.googleAnalytics.trackCookieConsent(hasConsented);
      },
    );

    this.statusChangeSubscription = this.ccService.statusChange$.subscribe(
      (event: NgcStatusChangeEvent) => {
        const hasConsented = event.status === 'allow';

        // Track cookie consent event when user accepts or refuses
        this.googleAnalytics.trackCookieConsent(hasConsented);

        // Reload page when user accepts cookies to ensure GA cookie is created
        if (hasConsented) {
          // Wait a bit longer to ensure cookie is saved and consent is processed
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      },
    );

    this.revokeChoiceSubscription = this.ccService.revokeChoice$.subscribe(
      () => {
        // you can use this.ccService.getConfig() to do stuff...
      },
    );

    this.noCookieLawSubscription = this.ccService.noCookieLaw$.subscribe(
      (event: NgcNoCookieLawEvent) => {
        // you can use this.ccService.getConfig() to do stuff...
      },
    );
    if (this.safeHasConsented()) {
      this.safeDestroy();
      // Track cookie consent on all pages after consent is given
      setTimeout(() => {
        this.googleAnalytics.trackCookieConsent(true);
      }, 500);
    }
    this.configureCookieConsent();
  }

  ngOnDestroy() {
    // unsubscribe to cookieconsent observables to prevent memory leaks
    this.popupOpenSubscription.unsubscribe();
    this.popupCloseSubscription.unsubscribe();
    this.initializeSubscription.unsubscribe();
    this.statusChangeSubscription.unsubscribe();
    this.revokeChoiceSubscription.unsubscribe();
    this.noCookieLawSubscription.unsubscribe();
  }
  initialize() {
    // App Check is optional. The installed @firebase/app-check is incompatible
    // with firebase@7 (@firebase/app 0.6.x), so guard it to avoid crashing the
    // whole app bootstrap if it throws at require/init time.
    try {
      // [START appcheck_initialize]
      const { initializeApp } = require('@firebase/app');
      const {
        initializeAppCheck,
        ReCaptchaV3Provider,
      } = require('@firebase/app-check');

      const app = initializeApp({
        // Your firebase configuration object
      });

      // Pass your reCAPTCHA v3 site key (public key) to activate(). Make sure this
      // key is the counterpart to the secret key you set in the Firebase console.
      console.log(environment.recaptcha);
      const appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(environment.recaptcha),

        // Optional argument. If true, the SDK automatically refreshes App Check
        // tokens as needed.
        isTokenAutoRefreshEnabled: true,
      });
      // [END appcheck_initialize]
    } catch (e) {
      console.error('App Check init skipped:', e);
    }
  }
  configureCookieConsent() {
    this.translate
      .get([
        'cookie.header',
        'cookie.message',
        'cookie.dismiss',
        'cookie.allow',
        'cookie.deny',
        'cookie.link',
        'cookie.policy',
      ])
      .subscribe((data) => {
        this.ccService.getConfig().content =
          this.ccService.getConfig().content || {};
        // Override default messages with the translated ones
        this.ccService.getConfig().content.header = data['cookie.header'];
        this.ccService.getConfig().content.message = data['cookie.message'];
        this.ccService.getConfig().content.dismiss = data['cookie.dismiss'];
        this.ccService.getConfig().content.allow = data['cookie.allow'];
        this.ccService.getConfig().content.deny = data['cookie.deny'];
        this.ccService.getConfig().content.link = data['cookie.link'];
        this.ccService.getConfig().content.policy = data['cookie.policy'];

        this.safeDestroy(); //remove previous cookie bar (with default messages)
        this.ccService.init(this.ccService.getConfig()); // update config with translated messages
      });
  }
}
